import { CommunicationDirectionCell } from '@/components/CommunicationDirectionCell';
import { CommunicationLogActions } from '@/components/CommunicationLogActions';
import { EmptyCell } from '@/components/EmptyCell';
import { SendSmsModal, SmsTarget } from '@/components/SendSmsModal';
import { useLiveGraphQL } from '@/hooks/useLiveGraphQL';
import { usePartnerAccess } from '@/hooks/usePartnerAccess';
import { getBariatricProcedureStageLabel } from '@/services/patient-fields';
import { getRowNavigationUrl } from '@/services/log-row-navigation';
import {
  getCommunicationContactName,
  getCommunicationContactReference,
  getExternalNumberFromPhoneCommunication,
  getPayloadByType,
} from '@bariendo/lib/communication';
import { CommunicationCategory, SMS_LOGS_PHONE_QUERY_PARAM, TEXT_CONTENT_PAYLOAD_ID } from '@bariendo/lib/constants';
import { isSamePhoneNumber } from '@bariendo/lib/phone-validations';
import { Anchor, Badge, Container, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { formatDateTime, isResource } from '@medplum/core';
import { Communication, Patient } from '@medplum/fhirtypes';
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

/**
 * The patient is selected for the Stage column as well as the name:
 * `getBariatricProcedureStageLabel` prefers the meta tag and falls back to the extension, and
 * selecting only one leaves most rows with an empty pill. `sender` and `recipient` are how a
 * partner text is recognised (inbound partner texts carry the Organization as `sender`,
 * outbound ones as `recipient`), so the Text action can pin the thread and the composer can
 * name them. Only the Organization branch of each is resolved: a second union branch that
 * also declares `name` at another type fails the whole query. Pinned by
 * `SmsMessagesPage.queries.test.ts`.
 */
export const SMS_MESSAGES_QUERY = `
    query {
      CommunicationList(category: "${CommunicationCategory.SMS_MESSAGE}", _sort: "-sent") {
        resourceType
        id
        sent
        subject {
          reference
          resource {
            ... on Patient {
              resourceType
              id
              name {
                family
                given
              }
              meta {
                tag {
                  system
                  code
                }
              }
              extension {
                url
                valueString
              }
            }
          }
        }
        sender {
          reference
          resource {
            ... on Organization {
              resourceType
              id
              name
            }
          }
        }
        recipient {
          reference
          resource {
            ... on Organization {
              resourceType
              id
              name
            }
          }
        }
        status
        category {
          coding {
            code
            display
          }
        }
        medium {
          coding {
            code
            display
          }
        }
        extension {
          url
          valueString
        }
        payload {
          id
          contentString
        }
      }
    }
  `;

/** Past this, a text is almost certainly clamped and gets a toggle; short ones show whole. */
const LONG_MESSAGE_CHARS = 160;
const MESSAGE_CLAMP_LINES = 3;

/**
 * A text as it was sent, not a one-line excerpt.
 *
 * For an unknown number this log is the only place the text can be read, so cutting it to
 * an ellipsis hides the one thing the row is for. Long texts clamp to three lines with a
 * toggle rather than growing the row to 1,600 characters; the toggle stops propagation
 * because a patient row navigates on click.
 */
function SmsMessageCell({ text }: { text?: string }): ReactElement {
  const [expanded, setExpanded] = useState(false);
  if (!text) {
    return <EmptyCell />;
  }
  const isLong = text.length > LONG_MESSAGE_CHARS || text.includes('\n');
  return (
    <Stack gap={2} align="flex-start">
      <Text
        size="sm"
        style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
        lineClamp={expanded ? undefined : MESSAGE_CLAMP_LINES}
      >
        {text}
      </Text>
      {isLong && (
        <Anchor
          component="button"
          type="button"
          size="xs"
          onClick={(event) => {
            event.stopPropagation();
            setExpanded((current) => !current);
          }}
        >
          {expanded ? 'Less' : 'More'}
        </Anchor>
      )}
    </Stack>
  );
}

export function SmsMessagesPage(): ReactElement {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const phoneFilter = searchParams.get(SMS_LOGS_PHONE_QUERY_PARAM);
  const [texting, setTexting] = useState<SmsTarget | undefined>();
  const [textOpened, { open: openText, close: closeText }] = useDisclosure(false);
  const hasPartnerAccess = usePartnerAccess();

  const {
    data: smsListFromGraphQL,
    loading,
    scrollPagination,
  } = useLiveGraphQL<Communication>(SMS_MESSAGES_QUERY, undefined, {
    enablePagination: true,
    pageSize: 50,
    addNewItemToTop: true,
  });

  const data = useMemo(() => {
    const list = smsListFromGraphQL ?? [];
    const term = phoneFilter?.trim();
    if (!term) {
      return list;
    }
    return list.filter((sms) => isSamePhoneNumber(getExternalNumberFromPhoneCommunication(sms), term));
  }, [smsListFromGraphQL, phoneFilter]);

  const openTextFor = useCallback(
    (sms: Communication, phoneNumber: string) => {
      setTexting({
        phoneNumber,
        recipient: getCommunicationContactReference(sms),
        displayName: getCommunicationContactName(sms),
      });
      openText();
    },
    [openText]
  );

  const columns = useMemo<MRT_ColumnDef<Communication>[]>(
    () => [
      {
        id: 'type',
        header: '',
        enableSorting: false,
        size: 90,
        enableGlobalFilter: false,
        accessorFn: (row) => row,
        Cell: ({ cell }) => <CommunicationDirectionCell communication={cell.getValue<Communication>()} />,
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        size: 90,
        enableGlobalFilter: false,
        accessorFn: (row) => row,
        Cell: ({ cell }) => {
          const sms = cell.getValue<Communication>();
          const phoneNumber = getExternalNumberFromPhoneCommunication(sms);
          return (
            <CommunicationLogActions
              phoneNumber={phoneNumber}
              recipient={getCommunicationContactReference(sms)}
              onText={() => phoneNumber && openTextFor(sms, phoneNumber)}
            />
          );
        },
      },
      {
        id: 'subject',
        header: 'Patient',
        size: 200,
        enableSorting: false,
        accessorFn: (sms) => getCommunicationContactName(sms) ?? '',
        Cell: ({ row }) => {
          const name = getCommunicationContactName(row.original);
          return name ? <Text size="sm">{name}</Text> : <EmptyCell />;
        },
      },
      {
        id: 'stage',
        header: 'Stage',
        size: 140,
        enableSorting: false,
        accessorFn: (sms) => {
          const patient = sms.subject?.resource;
          return isResource(patient, 'Patient') ? getBariatricProcedureStageLabel(patient as Patient) : '';
        },
        Cell: ({ cell }) => {
          const label = cell.getValue<string>();
          if (!label) {
            return <EmptyCell />;
          }
          return (
            <Badge size="sm" color="gray" variant="light" radius="xl">
              {label}
            </Badge>
          );
        },
      },
      {
        id: 'number',
        header: 'Number',
        size: 100,
        enableSorting: false,
        accessorFn: (sms) => getExternalNumberFromPhoneCommunication(sms),
        Cell: ({ cell }) => <Text size="sm">{cell.getValue<string>()}</Text>,
      },
      {
        id: 'content',
        header: 'Message',
        size: 420,
        enableSorting: false,
        accessorFn: (sms) => getPayloadByType(sms, TEXT_CONTENT_PAYLOAD_ID)?.contentString,
        Cell: ({ cell }) => <SmsMessageCell text={cell.getValue<string | undefined>()} />,
      },
      {
        id: 'sent',
        header: 'Date',
        size: 150,
        enableSorting: true,
        sortingFn: 'datetime',
        accessorFn: (sms) => sms.sent,
        Cell: ({ cell }) => <Text size="sm">{formatDateTime(cell.getValue<string>())}</Text>,
      },
      {
        id: 'status',
        header: 'Status',
        size: 100,
        enableSorting: false,
        accessorFn: (sms) => sms.status,
        Cell: ({ row }) => (
          <Text size="sm" tt="capitalize">
            {row.original.status}
          </Text>
        ),
      },
    ],
    [openTextFor]
  );

  const table = useMantineReactTable({
    columns,
    data,
    rowCount: data.length,
    globalFilterFn: 'contains',
    initialState: {
      density: 'xs' as const,
      sorting: [{ id: 'sent', desc: true }],
    },
    state: {
      isLoading: loading,
      showGlobalFilter: false,
    },
    enableRowSelection: false,
    enableSorting: true,
    enableColumnFilters: false,
    enableGlobalFilter: true,
    enablePagination: false,
    enableColumnActions: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableRowActions: false,
    enableRowVirtualization: true,
    enableSelectAll: false,
    enableToolbarInternalActions: false,
    mantineTableProps: {
      highlightOnHover: true,
    },
    mantineTableBodyRowProps: ({ row }) => {
      const url = getRowNavigationUrl(row.original, hasPartnerAccess);
      return {
        onClick: () => {
          if (url) {
            navigate(url);
          }
        },
        style: { cursor: url ? 'pointer' : 'default' },
      };
    },
    mantinePaperProps: {
      shadow: 'sm',
      withBorder: true,
      style: { height: '100%', display: 'flex', flexDirection: 'column' },
    },
    mantineTableContainerProps: scrollPagination?.tableContainerProps,
    mantineTableHeadCellProps: {
      style: {
        fontWeight: '500',
      },
    },
  });

  return (
    <Container
      size="xl"
      py="md"
      style={{
        height: 'calc(100vh - 60px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack gap="lg" style={{ flex: 1, overflow: 'hidden' }}>
        <Group justify="space-between" align="center">
          <Title order={1}>SMS logs</Title>
        </Group>

        <Paper style={{ flex: 1, overflow: 'hidden' }}>
          <MantineReactTable table={table} />
        </Paper>
      </Stack>

      <SendSmsModal target={texting} opened={textOpened} onClose={closeText} />
    </Container>
  );
}
